import { HttpClient } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import type {
  Form,
  FormField,
  ReactiveDropdownDataSource,
  ReactiveDropdownDataSourceContext,
} from '../../interfaces/form.interface';
import { DropdownData, type Value } from '../../interfaces/input.interface';
import type {
  ReactiveDropdownCacheStatus,
  ReactiveDropdownDiagnostics,
  ReactiveFieldLoadState,
} from '../../interfaces/reactive-field-runtime-state.interface';
import { ReactiveFormDefaultValueResolver } from '../../model/reactive-form-default-value.resolver';
import { ReactiveFormValueUtils } from '../../model/reactive-form-value.utils';
import {
  ReactiveDropdownCacheConfig,
  ReactiveDropdownCacheService,
} from '../reactive-dropdown-cache.service';
import {
  ReactiveChangeOriginPolicy,
  type ReactiveChangeOrigin,
} from '../change-origin/reactive-change-origin';
import { ReactiveDependencyGraph } from '../dependencies/reactive-dependency-graph';
import { ReactiveDropdownOptionNormalizer } from '../reactive-dropdown-option-normalizer';
import { ReactiveDropdownAutoSelectService } from './reactive-dropdown-auto-select.service';
import { ReactiveDropdownCacheCoordinator } from './reactive-dropdown-cache.coordinator';
import { ReactiveDropdownDependencyGraphStore } from './reactive-dropdown-dependency-graph.store';
import { ReactiveDropdownLoader } from './reactive-dropdown-loader';
import { ReactiveDropdownMapper } from './reactive-dropdown-mapper';
import { ReactiveDropdownStateStore } from './reactive-dropdown-state.store';
import {
  type ReactiveDropdownCatalogPhase,
  filterReactiveDropdownCatalogPhase,
  resolveReactiveDropdownCatalogPhase,
} from './reactive-dropdown-catalogs';
import {
  ReactiveOptionStoreService,
  type ReactiveOptionStoreEntry,
} from '../reactive-option-store.service';
import type { ReactiveFormBuilderStorageAdapter } from '../../adapters/reactive-form-builder.adapters';

export type ReactiveDropdownDependencyLoadOptions = {
  /** @deprecated Use origin: 'hydrate' instead. */
  readonly isHydration?: boolean;
  readonly origin?: ReactiveChangeOrigin;
};

type ReactiveDropdownEngineConfig<T> = {
  readonly http: HttpClient;
  readonly dropdownCache: ReactiveDropdownCacheService;
  readonly optionStore: ReactiveOptionStoreService;
  readonly getForm: () => Form<T> | null;
  readonly setForm: (form: Form<T>) => void;
  readonly getModel: () => FormGroup | null;
  readonly getPreviousData: () => T | null;
  readonly getServerUrl: () => string;
  readonly getAppName: () => string;
  readonly canMutateModelSafely: () => boolean;
  readonly isDestroyed: () => boolean;
  readonly applySelectedValue: (field: FormField<T>, value: unknown, origin: ReactiveChangeOrigin) => void;
  readonly applyResolvedFieldData: (fieldName: string, origin: ReactiveChangeOrigin) => Promise<void>;
  readonly completeFieldDataCascade: (fieldName: string, origin: ReactiveChangeOrigin) => void;
  readonly publishStableValueSnapshot: (fieldName: string, origin: ReactiveChangeOrigin) => void;
  readonly notifyStateChanged: () => void;
  readonly storage?: ReactiveFormBuilderStorageAdapter | undefined;
};


const DEFAULT_DROPDOWN_DEPENDENCY_LOAD_OPTIONS: ReactiveDropdownDependencyLoadOptions = {
  origin: 'programmatic',
};
const DEFERRED_DROPDOWN_LOAD_DELAY_MS = 0;
const RECENT_DROPDOWN_LOAD_DEDUPE_MS = 10000;

export class ReactiveDropdownEngine<T> {

  private readonly autoSelectService: ReactiveDropdownAutoSelectService<T>;
  private readonly cacheCoordinator: ReactiveDropdownCacheCoordinator;
  private readonly loader: ReactiveDropdownLoader;
  private readonly dependencyGraphStore = new ReactiveDropdownDependencyGraphStore<T>(() => this.form);
  private readonly mapper = new ReactiveDropdownMapper<T>();
  private readonly pendingDropdownSelections = new Map<string, readonly string[]>();
  private readonly stateStore = new ReactiveDropdownStateStore();
  private readonly defaultValueResolver = new ReactiveFormDefaultValueResolver<T>();

  constructor(private readonly config: ReactiveDropdownEngineConfig<T>) {
    this.autoSelectService = new ReactiveDropdownAutoSelectService<T>({
      getModel: () => this.model,
      applySelectedValue: (field, value, origin) => this.config.applySelectedValue(field, value, origin),
    });
    this.cacheCoordinator = new ReactiveDropdownCacheCoordinator(this.config.dropdownCache);
    this.loader = new ReactiveDropdownLoader({
      http: this.config.http,
      getServerUrl: () => this.config.getServerUrl(),
      resolveTechnicalErrorMessage: (error) => this.resolveTechnicalErrorMessage(error),
    });
  }

  public destroy(): void {
    this.pendingDropdownSelections.clear();
    this.stateStore.clear();
  }

  public capturePendingSelectionsFromPreviousData(): void {
    const form = this.form;
    const previousData = this.previousData;

    if (!form || previousData === null) {
      return;
    }

    for (const field of this.getDeclarativeDropdownFields()) {
      if (!this.shouldTrackPendingSelection(field)) {
        continue;
      }

      const value = this.defaultValueResolver.resolve(field, {
        form,
        previousData,
        externalDropdownsReady: false,
      });

      this.rememberPendingSelection(field, value);
    }
  }

  public capturePendingSelectionsFromCurrentModel(): void {
    for (const field of this.getDeclarativeDropdownFields()) {
      const fieldName = field.name ?? '';
      const control = fieldName !== '' ? this.model?.get(fieldName) : null;

      if (!control || !this.shouldTrackPendingSelection(field)) {
        continue;
      }

      this.rememberPendingSelection(field, control.value);
    }
  }

  public resolvePendingSelections(): void {
    for (const [fieldName] of this.pendingDropdownSelections) {
      const field = this.findFormField(fieldName);

      if (!field) {
        this.pendingDropdownSelections.delete(fieldName);
        continue;
      }

      this.resolvePendingSelectionForField(field, this.getOrCreateDropdownData(field), 'hydrate');
    }
  }

  public getFieldLoadStates(): ReadonlyMap<string, ReactiveFieldLoadState> {
    return this.stateStore.getFieldLoadStates();
  }

  public getFieldErrors(): ReadonlyMap<string, string> {
    return this.stateStore.getFieldErrors();
  }

  public getFieldWarnings(): ReadonlyMap<string, string> {
    return this.stateStore.getFieldWarnings();
  }

  public getFieldDiagnostics(): ReadonlyMap<string, ReactiveDropdownDiagnostics> {
    return this.stateStore.getFieldDiagnostics();
  }

  public async refresh(
    fieldName: string,
    options: ReactiveDropdownDependencyLoadOptions = DEFAULT_DROPDOWN_DEPENDENCY_LOAD_OPTIONS,
  ): Promise<void> {
    const field = this.findFormField(fieldName);

    if (!field?.dataSource) {
      return;
    }

    await this.loadDeclarativeDropdownField(field, fieldName, { ...options, origin: options.origin ?? 'dropdownRefresh' }, true);
  }

  public async loadOnDemand(
    fieldName: string,
    options: ReactiveDropdownDependencyLoadOptions = DEFAULT_DROPDOWN_DEPENDENCY_LOAD_OPTIONS,
  ): Promise<void> {
    const field = this.findFormField(fieldName);

    if (!field?.dataSource || this.resolveDropdownCatalogPhase(field) !== 'lazy' || this.hasResolvedDropdownOptions(field)) {
      return;
    }

    await this.loadDeclarativeDropdownField(field, null, { ...options, origin: options.origin ?? 'dropdownRefresh' });
  }

  public restoreCache(): void {
    this.forEachDeclarativeDropdownField((field, data) => {
      const cacheConfig = this.getDropdownCacheConfig(field);

      if (!cacheConfig) {
        return;
      }

      if (this.cacheCoordinator.restore(cacheConfig, data)) {
        return;
      }
    });
  }

  public saveCache(): void {
    this.forEachDeclarativeDropdownField((field, data) => {
      const cacheConfig = this.getDropdownCacheConfig(field);

      if (!cacheConfig) {
        return;
      }

      this.cacheCoordinator.saveIfAllowed(cacheConfig, data);
    });
  }

  public async loadInitial(): Promise<void> {
    if (!this.config.canMutateModelSafely()) {
      return;
    }

    const dropdownFields = this.getDeclarativeDropdownFields().filter((field) => this.shouldLoadCatalogField(field));
    const catalogDefinitions = this.form?.dropdownCatalogs ?? null;
    const criticalFields = filterReactiveDropdownCatalogPhase(dropdownFields, catalogDefinitions, 'critical');
    const deferredFields = filterReactiveDropdownCatalogPhase(dropdownFields, catalogDefinitions, 'deferred');

    await this.loadDeclarativeDropdownFields(criticalFields, null, { origin: 'programmatic' });
    this.scheduleDeferredDeclarativeDropdowns(deferredFields.map((field) => field.name ?? ''));
  }

  public async loadDependent(
    changedFields: readonly string[],
    options: ReactiveDropdownDependencyLoadOptions = DEFAULT_DROPDOWN_DEPENDENCY_LOAD_OPTIONS,
  ): Promise<void> {
    if (!this.config.canMutateModelSafely() || changedFields.length === 0) {
      return;
    }

    this.clearPendingSelectionsForUserChanges(changedFields, options);

    const dependencyFields = this.getDeclarativeDropdownFields()
      .filter((field) => this.hasChangedDependency(field, changedFields));
    const resetFields = dependencyFields.filter((field) => !this.dependenciesHaveValueOrCanUseCustomFilter(field));

    for (const field of resetFields) {
      this.resetDeclarativeDropdownForMissingDependencies(field, this.getChangedDependency(field, changedFields), options);
    }

    for (const field of resetFields) {
      const resetFieldName = field.name ?? '';

      if (resetFieldName !== '') {
        await this.afterDropdownDataChanged(resetFieldName);
      }
    }

    const localFields = dependencyFields
      .filter((field) => this.shouldFilterLocallyAfterDependencyChange(field, changedFields));

    for (const field of localFields) {
      await this.filterDeclarativeDropdownLocally(field, this.getChangedDependency(field, changedFields), options);
    }

    const remoteFields = dependencyFields
      .filter((field) => this.shouldLoadAfterDependencyChange(field, changedFields));

    await Promise.all(remoteFields.map(
      (field) => this.loadDeclarativeDropdownField(field, this.getChangedDependency(field, changedFields), options),
    ));
  }

  public getHydratedDependencyParents(): string[] {
    const dependencyParentFields = new Set<string>();

    for (const dependency of this.dependencyGraph.getHydratedSources(
      (fieldName) => this.model?.get(fieldName)?.value,
      'dropdown',
    )) {
      dependencyParentFields.add(dependency);
    }

    return Array.from(dependencyParentFields);
  }

  public resolveRawValue<TEntity = unknown>(fieldName: string, sourceValue: unknown): TEntity | null {
    const field = this.findFormField(fieldName);

    if (!field) {
      return this.toObjectRawValue<TEntity>(sourceValue);
    }

    return this.resolveDropdownRawValueForField<TEntity>(field, fieldName, sourceValue);
  }

  public resolveOption(fieldName: string, sourceValue: unknown): ReactiveOptionStoreEntry | null {
    const field = this.findFormField(fieldName);

    if (!field) {
      return null;
    }

    return this.resolveDropdownOptionForField(field, fieldName, sourceValue);
  }

  public resolveLabel(fieldName: string, sourceValue: unknown): string {
    const option = this.resolveOption(fieldName, sourceValue);

    return option?.label ?? '';
  }

  public resolveBackendValue(fieldName: string, sourceValue: unknown): unknown | null {
    const field = this.findFormField(fieldName);

    if (!field) {
      return sourceValue ?? null;
    }

    const backendValueKey = ReactiveFormValueUtils.getFieldBackendValueKey(field);
    const rawValue = this.resolveRawValue<Record<string, unknown>>(fieldName, sourceValue);

    if (rawValue !== null && Object.prototype.hasOwnProperty.call(rawValue, backendValueKey)) {
      return rawValue[backendValueKey] ?? null;
    }

    return sourceValue ?? null;
  }

  private get form(): Form<T> | null {
    return this.config.getForm();
  }

  private set form(form: Form<T>) {
    this.config.setForm(form);
  }

  private get model(): FormGroup | null {
    return this.config.getModel();
  }

  private get previousData(): T | null {
    return this.config.getPreviousData();
  }

  private get dependencyGraph(): ReactiveDependencyGraph<T> {
    return this.dependencyGraphStore.getGraph();
  }

  private async loadDeclarativeDropdownFields(
    fields: readonly FormField<T>[],
    changedField: string | null,
    options: ReactiveDropdownDependencyLoadOptions = DEFAULT_DROPDOWN_DEPENDENCY_LOAD_OPTIONS,
  ): Promise<void> {
    await Promise.all(fields.map((field) => this.loadDeclarativeDropdownField(field, changedField, options)));
  }

  private async loadDeclarativeDropdownField(
    field: FormField<T>,
    changedField: string | null,
    options: ReactiveDropdownDependencyLoadOptions = DEFAULT_DROPDOWN_DEPENDENCY_LOAD_OPTIONS,
    forceRefresh = false,
  ): Promise<void> {
    const dataSource = field.dataSource;
    const fieldName = field.name ?? '';
    const shouldRequireDependencyValues = field.dependencyBehaviour?.filterLocally !== true;

    if (!dataSource?.endpoint || fieldName === '') {
      return;
    }

    if (shouldRequireDependencyValues && !this.dependenciesHaveValue(field)) {
      this.resetDeclarativeDropdownForMissingDependencies(field, changedField, options);
      return;
    }

    const params = this.resolveDataSourceParams(dataSource, field, changedField, options);
    const loadKey = this.createDeclarativeDropdownLoadKey(field, params);
    const dropdownData = this.getOrCreateDropdownData(field);

    if (this.stateStore.getActiveLoadKey(fieldName) === loadKey) {
      return;
    }

    if (this.shouldSkipRecentlyResolvedLoad(loadKey, dropdownData)) {
      return;
    }

    const cacheConfig = this.getDropdownCacheConfig(field, changedField, options);
    const shouldForceRefresh = forceRefresh || dataSource.forceReload === true ||
      (changedField !== null && field.dependencyBehaviour?.reloadOnParentChange === true);

    if (!shouldForceRefresh && cacheConfig && this.cacheCoordinator.restore(cacheConfig, dropdownData)) {
      const cachedData = this.applyDependencyFilterToDropdownData(field, dropdownData, changedField, options);

      this.setFieldDiagnostics(fieldName, this.createDiagnostics({
        fieldName,
        catalogPhase: this.resolveDropdownCatalogPhase(field),
        loadState: this.getResolvedDataState(cachedData),
        cacheStatus: 'hit',
        data: cachedData,
        changedField,
        options,
        requestKey: loadKey,
      }));

      this.nextDeclarativeDropdownLoadVersion(fieldName);
      this.setFieldLoadStateForResolvedData(field, cachedData);
      this.replaceFieldDropdownData(fieldName, cachedData);

      this.resolvePendingSelectionForField(field, cachedData, this.resolveChangeOrigin(options));
      const didAutoSelect = this.autoSelectDeclarativeDropdownIfNeeded(field, cachedData, options);

      await this.afterDropdownDataChanged(fieldName);
      this.publishAutoSelectStableSnapshotIfNeeded(fieldName, didAutoSelect);
      this.stateStore.markLoadKeyResolved(loadKey, Date.now());
      return;
    }

    const loadVersion = this.nextDeclarativeDropdownLoadVersion(fieldName);
    const loadStartedAt = Date.now();

    this.setFieldDiagnostics(fieldName, this.createDiagnostics({
      fieldName,
      catalogPhase: this.resolveDropdownCatalogPhase(field),
      loadState: 'loading',
      cacheStatus: cacheConfig ? 'miss' : 'none',
      data: dropdownData,
      changedField,
      options,
      loadStartedAt,
      requestKey: loadKey,
    }));

    this.stateStore.addLoadKey(loadKey);
    this.stateStore.setActiveLoadKey(fieldName, loadKey);
    this.prepareDeclarativeDropdownForLoad(field, changedField, options);

    try {
      const fetchResult = await this.loader.fetch({
        endpoint: dataSource.endpoint,
        params,
        ...(dataSource.timeoutMs !== undefined ? { timeoutMs: dataSource.timeoutMs } : {}),
        ...(dataSource.retry?.attempts !== undefined ? { retryAttempts: dataSource.retry.attempts } : {}),
        ...(dataSource.retry?.delayMs !== undefined ? { retryDelayMs: dataSource.retry.delayMs } : {}),
      });
      const response = fetchResult.response;

      if (!this.isDeclarativeDropdownLoadCurrent(fieldName, loadVersion)) {
        return;
      }

      const nextData = this.cacheCoordinator.withProvenance(
        this.applyDependencyFilterToDropdownData(
          field,
          this.mapDeclarativeDropdownResponse(field, response, changedField, options),
          changedField,
          options,
        ),
        'network',
      );

      this.replaceFieldDropdownData(fieldName, nextData);
      this.setFieldLoadStateForResolvedData(field, nextData);

      this.resolvePendingSelectionForField(field, nextData, this.resolveChangeOrigin(options));
      const didAutoSelect = this.autoSelectDeclarativeDropdownIfNeeded(field, nextData, options);

      const nextDataCacheStatus: ReactiveDropdownCacheStatus = cacheConfig
        ? this.cacheCoordinator.saveIfAllowed(cacheConfig, nextData)
        : 'none';

      const loadFinishedAt = Date.now();

      this.setFieldDiagnostics(fieldName, this.createDiagnostics({
        fieldName,
        catalogPhase: this.resolveDropdownCatalogPhase(field),
        loadState: this.getResolvedDataState(nextData),
        cacheStatus: nextDataCacheStatus,
        data: nextData,
        changedField,
        options,
        loadStartedAt,
        loadFinishedAt,
        retryCount: fetchResult.retryCount,
        requestKey: loadKey,
      }));
      this.stateStore.markLoadKeyResolved(loadKey, loadFinishedAt);

      await this.afterDropdownDataChanged(fieldName);
      this.publishAutoSelectStableSnapshotIfNeeded(fieldName, didAutoSelect);
    } catch (error: unknown) {
      if (this.isDeclarativeDropdownLoadCurrent(fieldName, loadVersion)) {
        const errorMessage = this.toErrorMessage(error);
        const loadFinishedAt = Date.now();

        this.setFieldLoadState(fieldName, 'error', errorMessage);
        this.setFieldDiagnostics(fieldName, this.createDiagnostics({
          fieldName,
          catalogPhase: this.resolveDropdownCatalogPhase(field),
          loadState: 'error',
          cacheStatus: cacheConfig ? 'miss' : 'none',
          data: dropdownData,
          changedField,
          options,
          loadStartedAt,
          loadFinishedAt,
          errorMessage,
          requestKey: loadKey,
        }));
        this.config.notifyStateChanged();
      }
    } finally {
      this.stateStore.deleteLoadKey(loadKey);

      const isCurrentActiveLoad = this.isDeclarativeDropdownLoadCurrent(fieldName, loadVersion) &&
        this.stateStore.getActiveLoadKey(fieldName) === loadKey;

      if (isCurrentActiveLoad) {
        this.stateStore.clearActiveLoadKey(fieldName);
      }
    }
  }

  private async afterDropdownDataChanged(fieldName: string): Promise<void> {
    await this.config.applyResolvedFieldData(fieldName, 'optionsLoaded');
    await this.loadDependent([fieldName], { origin: 'optionsLoaded' });
    this.config.completeFieldDataCascade(fieldName, 'optionsLoaded');
  }

  private prepareDeclarativeDropdownForLoad(
    field: FormField<T>,
    changedField: string | null,
    options: ReactiveDropdownDependencyLoadOptions,
  ): void {
    const fieldName = field.name ?? '';

    if (fieldName === '') {
      return;
    }

    const keepCurrentOptions = this.shouldKeepCurrentDropdownOptionsDuringLoad(field, changedField, options);
    const mustClearControl = this.shouldClearDependentDropdownControl(field, changedField, options) && !keepCurrentOptions;

    this.setFieldLoadState(fieldName, 'loading');

    if (!keepCurrentOptions) {
      const currentData = this.getOrCreateDropdownData(field);
      const loadingData = this.shouldShowLoadingOptionDuringReload(field)
        ? this.buildLoadingDropdownData(currentData, field)
        : {
          ...currentData,
          values: [],
          filtered: [],
        };

      this.replaceFieldDropdownData(fieldName, loadingData);
    }

    if (mustClearControl) {
      this.clearDropdownControl(field);
    }

    this.config.notifyStateChanged();
  }

  private shouldKeepCurrentDropdownOptionsDuringLoad(
    field: FormField<T>,
    changedField: string | null,
    options: ReactiveDropdownDependencyLoadOptions,
  ): boolean {
    if (this.isHydrationSeedDropdownData(this.getOrCreateDropdownData(field))) {
      return !this.shouldClearDependentDropdownControl(field, changedField, options);
    }

    if (changedField === null) {
      return false;
    }

    return field.dependencyBehaviour?.keepValueUntilOptionsLoaded === true ||
      field.dependencyBehaviour?.keepCurrentValueIfStillAvailable === true ||
      field.dependencyBehaviour?.preservePanelOnReload === true ||
      this.shouldPreserveDependentValueDuringHydration(field, options);
  }

  private shouldShowLoadingOptionDuringReload(field: FormField<T>): boolean {
    return field.dependencyBehaviour?.showLoadingOptionDuringReload === true &&
      (ReactiveFormValueUtils.isSingleDropdownField(field) || ReactiveFormValueUtils.isMultiSelectField(field));
  }

  private buildLoadingDropdownData(currentData: DropdownData<unknown>, field: FormField<T>): DropdownData<unknown> {
    const label = `Cargando ${String(field.label ?? 'opciones').toLowerCase()}...`;
    const loadingOption: Value<unknown> = {
      label,
      value: null,
      disabled: true,
    };

    return {
      ...currentData,
      values: [loadingOption],
      filtered: [loadingOption],
    };
  }

  private shouldClearDependentDropdownControl(
    field: FormField<T>,
    changedField: string | null,
    options: ReactiveDropdownDependencyLoadOptions,
  ): boolean {
    if (changedField === null || field.dependencyBehaviour?.clearOnParentChange === false) {
      return false;
    }

    if (this.shouldPreserveDependentValueDuringHydration(field, options)) {
      return false;
    }

    const changeOrigin = this.resolveChangeOrigin(options);

    if (field.dependencyBehaviour?.preserveOnOrigins?.includes(changeOrigin) === true) {
      return false;
    }

    if (field.dependencyBehaviour?.clearOnOrigins?.includes(changeOrigin) === true) {
      return true;
    }

    return ReactiveChangeOriginPolicy.shouldClearDependentValue(changeOrigin);
  }

  private shouldPreserveDependentValueDuringHydration(
    field: FormField<T>,
    options: ReactiveDropdownDependencyLoadOptions,
  ): boolean {
    return this.resolveChangeOrigin(options) === 'hydrate' && field.dependencyBehaviour?.clearOnHydration === false;
  }

  private autoSelectDeclarativeDropdownIfNeeded(
    field: FormField<T>,
    data: DropdownData<unknown>,
    options: ReactiveDropdownDependencyLoadOptions,
  ): boolean {
    return this.autoSelectService.autoSelect(field, data, this.resolveChangeOrigin(options));
  }

  private publishAutoSelectStableSnapshotIfNeeded(fieldName: string, didAutoSelect: boolean): void {
    if (didAutoSelect) {
      this.config.publishStableValueSnapshot(fieldName, 'autoSelect');
    }
  }

  private clearDropdownControl(field: FormField<T>): void {
    const fieldName = field.name ?? '';
    const control = fieldName !== '' ? this.model?.get(fieldName) : null;

    if (!control) {
      return;
    }

    const emptyValue = ReactiveFormValueUtils.isMultiSelectField(field) ? [] : null;

    this.pendingDropdownSelections.delete(fieldName);
    this.model?.patchValue({ [fieldName]: emptyValue }, { emitEvent: false });
    control.markAsPristine();
    control.markAsUntouched();
  }

  private shouldTrackPendingSelection(field: FormField<T>): boolean {
    return ReactiveFormValueUtils.isSingleDropdownField(field) || ReactiveFormValueUtils.isMultiSelectField(field);
  }

  private rememberPendingSelection(field: FormField<T>, value: unknown): void {
    const fieldName = field.name ?? '';

    if (fieldName === '') {
      return;
    }

    const pendingIds = this.getPendingSelectionIds(field, value);

    if (pendingIds.length === 0) {
      return;
    }

    this.pendingDropdownSelections.set(fieldName, pendingIds);
  }

  private clearPendingSelectionsForUserChanges(
    changedFields: readonly string[],
    options: ReactiveDropdownDependencyLoadOptions,
  ): void {
    if (this.resolveChangeOrigin(options) !== 'user' || changedFields.length === 0 || this.pendingDropdownSelections.size === 0) {
      return;
    }

    for (const [fieldName] of this.pendingDropdownSelections) {
      const field = this.findFormField(fieldName);

      if (!field || changedFields.includes(fieldName) || this.hasChangedDependency(field, changedFields)) {
        this.pendingDropdownSelections.delete(fieldName);
      }
    }
  }

  private resolvePendingSelectionForField(
    field: FormField<T>,
    data: DropdownData<unknown>,
    origin: ReactiveChangeOrigin,
  ): boolean {
    const fieldName = field.name ?? '';
    const pendingIds = fieldName !== '' ? this.pendingDropdownSelections.get(fieldName) : undefined;

    if (fieldName === '' || pendingIds === undefined || pendingIds.length === 0) {
      return false;
    }

    const control = this.model?.get(fieldName);

    if (!control || !control.pristine) {
      return false;
    }

    const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(field);
    const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);
    const options = ReactiveFormValueUtils.getDropdownOptions(data);
    const optionsIndex = ReactiveFormValueUtils.createOptionsIndex(options, primaryKey, identityKeys);
    const resolvedValues = pendingIds
      .map((id) => ReactiveFormValueUtils.mapIdToOptionValue(id, options, primaryKey, optionsIndex, identityKeys))
      .filter((value) => value !== null);

    if (resolvedValues.length !== pendingIds.length) {
      return false;
    }

    const isMulti = ReactiveFormValueUtils.isMultiSelectField(field);
    const nextValue = isMulti ? resolvedValues : resolvedValues[0] ?? null;

    if (this.isResolvedPendingSelection(field, control.value, nextValue, isMulti)) {
      this.pendingDropdownSelections.delete(fieldName);
      this.config.applySelectedValue(field, control.value, origin);
      return false;
    }

    control.patchValue(nextValue, { emitEvent: false });
    control.markAsPristine();
    control.markAsUntouched();
    this.config.applySelectedValue(field, nextValue, origin);
    this.pendingDropdownSelections.delete(fieldName);

    return true;
  }

  private isResolvedPendingSelection(
    field: FormField<T>,
    currentValue: unknown,
    nextValue: unknown,
    isMulti: boolean,
  ): boolean {
    const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(field);
    const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);

    return ReactiveFormValueUtils.areValuesEquivalent(currentValue, nextValue, primaryKey, identityKeys) &&
      this.hasResolvedControlValue(currentValue, isMulti, primaryKey, identityKeys);
  }

  private hasResolvedControlValue(
    value: unknown,
    isMulti: boolean,
    primaryKey: string,
    identityKeys: readonly string[],
  ): boolean {
    if (isMulti) {
      return Array.isArray(value) && value.length > 0 && value.every((item) =>
        item !== null && typeof item === 'object' &&
        ReactiveFormValueUtils.extractComparableId(item, primaryKey, identityKeys) !== null
      );
    }

    return value !== null && typeof value === 'object' &&
      ReactiveFormValueUtils.extractComparableId(value, primaryKey, identityKeys) !== null;
  }

  private getPendingSelectionIds(field: FormField<T>, value: unknown): string[] {
    const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(field);
    const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);
    const values = Array.isArray(value) ? value : this.splitPendingSelectionValue(value);
    const ids = new Set<string>();

    for (const item of values) {
      const comparableId = ReactiveFormValueUtils.extractComparableId(item, primaryKey, identityKeys);

      if (comparableId !== null) {
        ids.add(String(comparableId));
      }
    }

    return Array.from(ids);
  }

  private splitPendingSelectionValue(value: unknown): unknown[] {
    if (!ReactiveFormValueUtils.hasNonEmptyValue(value)) {
      return [];
    }

    if (typeof value === 'string') {
      return ReactiveFormValueUtils.splitSerializedIds(value);
    }

    return [value];
  }

  private getDeclarativeDropdownFields(): FormField<T>[] {
    const fields: FormField<T>[] = [];

    for (const section of this.form?.sections ?? []) {
      for (const field of section.fields ?? []) {
        if (field.dataSource?.endpoint) {
          fields.push(field);
        }
      }
    }

    return fields;
  }

  private shouldLoadCatalogField(field: FormField<T>): boolean {
    if (field.dependencyBehaviour?.filterLocally === true) {
      return this.dependenciesHaveValueOrCanUseCustomFilter(field) && !this.hasResolvedDropdownOptions(field);
    }

    if (!this.dependenciesHaveValue(field)) {
      return false;
    }

    return !this.hasResolvedDropdownOptions(field);
  }

  private scheduleDeferredDeclarativeDropdowns(fieldNames: readonly string[]): void {
    const normalizedFieldNames = fieldNames.filter((fieldName) => fieldName.trim() !== '');

    if (normalizedFieldNames.length === 0) {
      return;
    }

    this.scheduleNextFrame(() => {
      void this.loadDeferredDeclarativeDropdowns(normalizedFieldNames).catch((error: unknown) => {
        console.error('[ReactiveDropdownEngine] Failed to load deferred dropdown catalogs.', error);
      });
    });
  }

  private async loadDeferredDeclarativeDropdowns(fieldNames: readonly string[]): Promise<void> {
    if (!this.config.canMutateModelSafely() || this.config.isDestroyed()) {
      return;
    }

    const fields = fieldNames
      .map((fieldName) => this.findFormField(fieldName))
      .filter((field): field is FormField<T> => field !== null)
      .filter((field) => this.shouldLoadCatalogField(field));

    await this.loadDeclarativeDropdownFields(fields, null, { origin: 'programmatic' });
  }

  private scheduleNextFrame(callback: () => void): void {
    if (typeof globalThis.requestAnimationFrame === 'function') {
      globalThis.requestAnimationFrame(callback);

      return;
    }

    globalThis.setTimeout(callback, DEFERRED_DROPDOWN_LOAD_DELAY_MS);
  }

  private shouldLoadAfterDependencyChange(field: FormField<T>, changedFields: readonly string[]): boolean {
    if (!this.dependencyGraph.hasChangedDependency(field.name, changedFields, 'dropdown')) {
      return false;
    }

    if (this.resolveDropdownCatalogPhase(field) === 'lazy' && !this.hasResolvedDropdownOptions(field)) {
      return false;
    }

    if (field.dependencyBehaviour?.filterLocally === true) {
      return !this.hasResolvedDropdownOptions(field) && this.dependenciesHaveValueOrCanUseCustomFilter(field);
    }

    return field.dependencyBehaviour?.reloadOnParentChange === true && this.dependenciesHaveValue(field);
  }

  private resolveDropdownCatalogPhase(field: FormField<T>): ReactiveDropdownCatalogPhase {
    return resolveReactiveDropdownCatalogPhase(field, this.form?.dropdownCatalogs ?? null);
  }

  private hasResolvedDropdownOptions(field: FormField<T>): boolean {
    const data = this.getOrCreateDropdownData(field);

    return ReactiveFormValueUtils.hasRealDropdownOptions(data) && !this.isHydrationSeedDropdownData(data);
  }

  private isHydrationSeedDropdownData(data: DropdownData<unknown>): boolean {
    return this.cacheCoordinator.resolveProvenance(data) === 'hydrationSeed';
  }

  private hasChangedDependency(field: FormField<T>, changedFields: readonly string[]): boolean {
    return this.dependencyGraph.hasChangedDependency(field.name, changedFields, 'dropdown');
  }

  private resetDeclarativeDropdownForMissingDependencies(
    field: FormField<T>,
    changedField: string | null,
    options: ReactiveDropdownDependencyLoadOptions,
  ): void {
    const fieldName = field.name ?? '';

    if (fieldName === '') {
      return;
    }

    this.nextDeclarativeDropdownLoadVersion(fieldName);
    this.stateStore.clearActiveLoadKey(fieldName);

    const currentData = this.getOrCreateDropdownData(field);

    this.replaceFieldDropdownData(fieldName, {
      ...currentData,
      values: [],
      filtered: [],
    });

    if (this.shouldClearDependentDropdownControl(field, changedField, options)) {
      this.clearDropdownControl(field);
    }

    this.setFieldLoadState(fieldName, 'idle');
    this.config.notifyStateChanged();
  }

  private shouldFilterLocallyAfterDependencyChange(field: FormField<T>, changedFields: readonly string[]): boolean {
    return field.dependencyBehaviour?.filterLocally === true &&
      this.dependenciesHaveValueOrCanUseCustomFilter(field) &&
      this.dependencyGraph.hasChangedDependency(field.name, changedFields, 'dropdown');
  }

  private async filterDeclarativeDropdownLocally(
    field: FormField<T>,
    changedField: string | null,
    options: ReactiveDropdownDependencyLoadOptions,
  ): Promise<void> {
    const fieldName = field.name ?? '';

    if (fieldName === '') {
      return;
    }

    this.nextDeclarativeDropdownLoadVersion(fieldName);
    this.stateStore.clearActiveLoadKey(fieldName);

    const currentData = this.getOrCreateDropdownData(field);
    const shouldClearControl = this.shouldClearDependentDropdownControl(field, changedField, options);
    const nextData = this.applyDependencyFilterToDropdownData(field, {
      ...currentData,
    }, changedField, options);

    this.replaceFieldDropdownData(fieldName, nextData);
    this.setFieldLoadStateForResolvedData(field, nextData);

    if (shouldClearControl) {
      this.clearDropdownControl(field);
    }

    this.resolvePendingSelectionForField(field, nextData, this.resolveChangeOrigin(options));
    const didAutoSelect = this.autoSelectDeclarativeDropdownIfNeeded(field, nextData, options);

    await this.afterDropdownDataChanged(fieldName);
    this.publishAutoSelectStableSnapshotIfNeeded(fieldName, didAutoSelect);
  }

  private applyDependencyFilterToDropdownData(
    field: FormField<T>,
    data: DropdownData<unknown>,
    changedField: string | null,
    options: ReactiveDropdownDependencyLoadOptions,
  ): DropdownData<unknown> {
    if (field.dependencyBehaviour?.filterLocally !== true) {
      return data;
    }

    const values = Array.isArray(data.values) ? data.values : [];
    const hasCustomDependencyFilter = typeof field.dataSource?.filterOption === 'function';
    const canFilterOptions = hasCustomDependencyFilter || this.dependenciesHaveValue(field);
    const filtered = canFilterOptions
      ? values.filter((option) => this.doesOptionMatchDependencies(field, option, changedField, options))
      : [];

    return {
      ...data,
      values: [...values],
      filtered,
    };
  }

  private doesOptionMatchDependencies(
    field: FormField<T>,
    option: unknown,
    changedField: string | null,
    options: ReactiveDropdownDependencyLoadOptions,
  ): boolean {
    const dataSource = field.dataSource;
    const context = this.createDataSourceContext(field, changedField, this.resolveChangeOrigin(options));

    if (dataSource?.filterOption) {
      return dataSource.filterOption(option as Value<unknown>, context);
    }

    const optionRecord = this.toDependencyComparableRecord(option);

    if (!optionRecord) {
      return true;
    }

    return this.dependencyGraph.getSourcesForTarget(field.name, 'dropdown').every((dependency) => {
      const parentValue = this.model?.get(dependency)?.value;

      if (!ReactiveFormValueUtils.hasNonEmptyValue(parentValue)) {
        return false;
      }

      if (!(dependency in optionRecord)) {
        return true;
      }

      return this.doesDependencyValueMatch(optionRecord[dependency], parentValue);
    });
  }

  private toDependencyComparableRecord(option: unknown): Record<string, unknown> | null {
    if (option !== null && typeof option === 'object') {
      const optionRecord = option as Record<string, unknown>;
      const value = optionRecord['value'];

      if (value !== null && typeof value === 'object') {
        return value as Record<string, unknown>;
      }

      return optionRecord;
    }

    return null;
  }

  private doesDependencyValueMatch(optionValue: unknown, parentValue: unknown): boolean {
    const optionIds = this.toComparableDependencyIds(optionValue);
    const parentIds = this.toComparableDependencyIds(parentValue);

    if (optionIds.length === 0 || parentIds.length === 0) {
      return false;
    }

    return optionIds.some((optionId) => parentIds.includes(optionId));
  }

  private toComparableDependencyIds(value: unknown): string[] {
    const values = Array.isArray(value) ? value : [value];

    return values
      .map((item) => ReactiveFormValueUtils.extractComparableId(item, 'id') ?? item)
      .filter((item) => ReactiveFormValueUtils.hasNonEmptyValue(item))
      .map((item) => String(item));
  }

  private getChangedDependency(field: FormField<T>, changedFields: readonly string[]): string | null {
    return this.dependencyGraph.getChangedDependency(field.name, changedFields, 'dropdown');
  }

  private dependenciesHaveValue(field: FormField<T>): boolean {
    return this.dependencyGraph.dependenciesHaveValue(
      field.name,
      (dependency) => this.model?.get(dependency)?.value,
      'dropdown',
    );
  }

  private dependenciesHaveValueOrCanUseCustomFilter(field: FormField<T>): boolean {
    return this.dependenciesHaveValue(field) || this.canUseCustomDependencyFilter(field);
  }

  private canUseCustomDependencyFilter(field: FormField<T>): boolean {
    return field.dependencyBehaviour?.filterLocally === true && typeof field.dataSource?.filterOption === 'function';
  }

  private resolveChangeOrigin(options: ReactiveDropdownDependencyLoadOptions): ReactiveChangeOrigin {
    return ReactiveChangeOriginPolicy.resolve(
      options.origin,
      options.isHydration === true ? 'hydrate' : 'programmatic',
    );
  }

  private getOrCreateDropdownData(field: FormField<T>): DropdownData<unknown> {
    const currentData = field.input?.data as DropdownData<unknown> | undefined;

    if (currentData?.values && currentData?.filtered) {
      return currentData;
    }

    return {
      values: [],
      filtered: [],
      label: currentData?.label ?? '',
    };
  }

  private findFormField(fieldName: string): FormField<T> | null {
    for (const section of this.form?.sections ?? []) {
      for (const field of section.fields ?? []) {
        if (field.name === fieldName) {
          return field;
        }
      }
    }

    return null;
  }

  private resolveDropdownRawValueForField<TEntity = unknown>(
    field: FormField<T>,
    fieldName: string,
    sourceValue: unknown,
  ): TEntity | null {
    const option = this.resolveDropdownOptionForField(field, fieldName, sourceValue);

    if (option?.raw !== null && option?.raw !== undefined) {
      return option.raw as TEntity;
    }

    return this.toObjectRawValue<TEntity>(sourceValue);
  }

  private resolveDropdownOptionForField(
    field: FormField<T>,
    fieldName: string,
    sourceValue: unknown,
  ): ReactiveOptionStoreEntry | null {
    const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(field);
    const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);
    const optionFromStore = this.config.optionStore.getOption(fieldName, sourceValue, primaryKey, identityKeys);

    if (optionFromStore !== null) {
      return optionFromStore;
    }

    const dropdownData = field.input?.data as DropdownData<unknown> | undefined;
    const comparableValue = ReactiveFormValueUtils.extractComparableId(sourceValue, primaryKey, identityKeys);

    if (comparableValue === null) {
      return null;
    }

    const matchedOption = ReactiveFormValueUtils.findUniqueOptionByComparableKey(
      comparableValue,
      ReactiveFormValueUtils.getDropdownOptions(dropdownData),
      primaryKey,
      identityKeys,
    );

    if (!matchedOption) {
      return null;
    }

    return {
      key: String(comparableValue),
      value: matchedOption.value,
      label: String(matchedOption.label ?? ''),
      raw: matchedOption.raw ?? matchedOption.value,
      group: String(matchedOption.group ?? ''),
      disabled: Boolean(matchedOption.disabled ?? false),
    };
  }

  private toObjectRawValue<TEntity = unknown>(value: unknown): TEntity | null {
    return value !== null && typeof value === 'object' ? value as TEntity : null;
  }

  private mapDeclarativeDropdownResponse(
    field: FormField<T>,
    response: unknown,
    changedField: string | null,
    options: ReactiveDropdownDependencyLoadOptions,
  ): DropdownData<unknown> {
    return this.mapper.mapResponse({
      field,
      response,
      context: this.createDataSourceContext(field, changedField, this.resolveChangeOrigin(options)),
      currentData: this.getOrCreateDropdownData(field),
    });
  }

  private isDropdownData(value: unknown): value is DropdownData<unknown> {
    return this.mapper.isDropdownData(value);
  }

  private replaceFieldDropdownData(fieldName: string, data: DropdownData<unknown>): void {
    const form = this.form;

    if (!form) {
      return;
    }

    this.form = {
      ...form,
      sections: (form.sections ?? []).map((section) => ({
        ...section,
        fields: (section.fields ?? []).map((field) => {
          if (field.name !== fieldName) {
            return field;
          }

          const normalizedData = ReactiveDropdownOptionNormalizer.normalizeData(
            data,
            field.input?.dropdownConfiguration,
          );
          const currentData = field.input?.data;
          const nextData = this.isDropdownData(currentData) ? currentData : normalizedData;

          if (this.isDropdownData(currentData)) {
            currentData.values = normalizedData.values;
            currentData.filtered = normalizedData.filtered;
            currentData.label = normalizedData.label ?? '';
          }

          this.config.optionStore.setOptions(
            fieldName,
            nextData,
            ReactiveFormValueUtils.getFieldControlValueKey(field),
            ReactiveFormValueUtils.getFieldRawIdentityKeys(field),
          );

          return {
            ...field,
            input: {
              ...field.input,
              data: nextData,
            },
          };
        }),
      })),
    };
  }

  private setFieldLoadState(fieldName: string, loadState: ReactiveFieldLoadState, errorMessage: string | null = null): void {
    this.stateStore.setFieldLoadState(fieldName, loadState, errorMessage);
  }

  private setFieldLoadStateForResolvedData(field: FormField<T>, data: DropdownData<unknown>): void {
    const fieldName = field.name ?? '';

    if (fieldName === '') {
      return;
    }

    const missingValueMessage = this.getMissingSelectedDropdownValueMessage(field, data);

    this.setFieldLoadState(fieldName, this.getResolvedDataState(data));
    this.setFieldWarning(fieldName, missingValueMessage);
  }

  private getMissingSelectedDropdownValueMessage(field: FormField<T>, data: DropdownData<unknown>): string | null {
    if (field.dependencyBehaviour?.warnWhenSelectedValueMissing !== true) {
      return null;
    }

    const fieldName = field.name ?? '';
    const control = fieldName !== '' ? this.model?.get(fieldName) : null;
    const currentValue = control?.value;

    if (!ReactiveFormValueUtils.hasNonEmptyValue(currentValue)) {
      return null;
    }

    const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(field);
    const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);
    const options = ReactiveFormValueUtils.getDropdownOptions(data);

    if (this.autoSelectService.isCurrentValueAvailableInOptions(currentValue, options, primaryKey, identityKeys)) {
      return null;
    }

    const savedValue = ReactiveFormValueUtils.formatPreviewValue(currentValue, field);
    const label = field.label ?? fieldName;
    const savedSuffix = savedValue !== '' ? ` Valor guardado: ${savedValue}.` : '';

    return `El valor guardado de ${label} ya no existe en el catálogo cargado.${savedSuffix} ` +
      'Selecciona una opción actual antes de guardar.';
  }

  private setFieldWarning(fieldName: string, warningMessage: string | null): void {
    this.stateStore.setFieldWarning(fieldName, warningMessage);
  }

  private setFieldDiagnostics(fieldName: string, diagnostics: ReactiveDropdownDiagnostics): void {
    this.stateStore.setFieldDiagnostics(fieldName, diagnostics);
  }

  private createDiagnostics(params: {
    readonly fieldName: string;
    readonly catalogPhase: ReactiveDropdownCatalogPhase;
    readonly loadState: ReactiveFieldLoadState;
    readonly cacheStatus: ReactiveDropdownCacheStatus;
    readonly data: DropdownData<unknown>;
    readonly changedField: string | null;
    readonly options: ReactiveDropdownDependencyLoadOptions;
    readonly loadStartedAt?: number;
    readonly loadFinishedAt?: number;
    readonly retryCount?: number;
    readonly requestKey?: string;
    readonly errorMessage?: string | null;
  }): ReactiveDropdownDiagnostics {
    const values = Array.isArray(params.data.values) ? params.data.values : [];
    const filtered = Array.isArray(params.data.filtered) ? params.data.filtered : [];
    const loadDurationMs = params.loadStartedAt !== undefined && params.loadFinishedAt !== undefined
      ? Math.max(0, params.loadFinishedAt - params.loadStartedAt)
      : undefined;
    const warningMessage = this.stateStore.getFieldWarning(params.fieldName);

    return {
      fieldName: params.fieldName,
      loadState: params.loadState,
      cacheStatus: params.cacheStatus,
      optionCount: values.length,
      filteredOptionCount: filtered.length,
      retryCount: params.retryCount ?? 0,
      changedField: params.changedField,
      changeOrigin: this.resolveChangeOrigin(params.options),
      catalogPhase: params.catalogPhase,
      ...(params.loadStartedAt !== undefined ? { loadStartedAt: params.loadStartedAt } : {}),
      ...(params.loadFinishedAt !== undefined ? { loadFinishedAt: params.loadFinishedAt } : {}),
      ...(loadDurationMs !== undefined ? { loadDurationMs } : {}),
      ...(params.requestKey ? { requestKey: params.requestKey } : {}),
      ...(params.errorMessage ? { errorMessage: params.errorMessage } : {}),
      dataProvenance: this.cacheCoordinator.resolveProvenance(params.data),
      ...(warningMessage ? { warningMessage } : {}),
    };
  }

  private getResolvedDataState(data: DropdownData<unknown>): ReactiveFieldLoadState {
    return ReactiveFormValueUtils.hasRealDropdownOptions(data) ? 'ready' : 'empty';
  }

  private createDeclarativeDropdownLoadKey(field: FormField<T>, params: Record<string, unknown>): string {
    return [field.name ?? '', field.dataSource?.cacheKey ?? '', JSON.stringify(params)].join(':');
  }

  private shouldSkipRecentlyResolvedLoad(loadKey: string, dropdownData: DropdownData<unknown>): boolean {
    const dataProvenance = this.cacheCoordinator.resolveProvenance(dropdownData);
    const hasResolvedData = ReactiveFormValueUtils.hasRealDropdownOptions(dropdownData) ||
      dataProvenance === 'cache' ||
      dataProvenance === 'network';

    return hasResolvedData && this.stateStore.wasLoadKeyRecentlyResolved(
      loadKey,
      Date.now(),
      RECENT_DROPDOWN_LOAD_DEDUPE_MS,
    );
  }

  private nextDeclarativeDropdownLoadVersion(fieldName: string): number {
    return this.stateStore.nextLoadVersion(fieldName);
  }

  private isDeclarativeDropdownLoadCurrent(fieldName: string, loadVersion: number): boolean {
    return this.stateStore.isLoadCurrent(fieldName, loadVersion, this.config.isDestroyed());
  }

  private createDataSourceContext(
    field: FormField<T>,
    changedField: string | null,
    changeOrigin: ReactiveChangeOrigin,
  ): ReactiveDropdownDataSourceContext<T> {
    const selectedRawValues = this.getSelectedRawValues();

    return {
      field,
      model: this.model,
      previousData: this.previousData,
      changedField,
      changeOrigin,
      selectedRawValues,
      getSelectedRaw: <TEntity = unknown>(fieldName: string): TEntity | null => {
        const selectedRawValue = selectedRawValues[fieldName];

        return selectedRawValue !== undefined
          ? selectedRawValue as TEntity | null
          : this.resolveRawValue<TEntity>(fieldName, this.model?.get(fieldName)?.value);
      },
      getSelectedLabel: (fieldName: string): string =>
        this.resolveLabel(fieldName, this.model?.get(fieldName)?.value),
      getSelectedBackendValue: (fieldName: string): unknown | null =>
        this.resolveBackendValue(fieldName, this.model?.get(fieldName)?.value),
    };
  }

  private getSelectedRawValues(): Record<string, unknown | null> {
    const selectedRawValues: Record<string, unknown | null> = {};

    for (const section of this.form?.sections ?? []) {
      for (const field of section.fields ?? []) {
        const fieldName = field.name ?? '';

        if (fieldName === '' || !this.model?.get(fieldName)) {
          continue;
        }

        const dropdownData = field.input?.data as DropdownData<unknown> | undefined;

        if (!ReactiveFormValueUtils.isDropdownLikeField(field, dropdownData)) {
          continue;
        }

        selectedRawValues[fieldName] = this.resolveDropdownRawValueForField(
          field,
          fieldName,
          this.model.get(fieldName)?.value,
        );
      }
    }

    return selectedRawValues;
  }

  private forEachDeclarativeDropdownField(callback: (field: FormField<T>, data: DropdownData<unknown>) => void): void {
    for (const section of this.form?.sections ?? []) {
      for (const field of section.fields ?? []) {
        const dataSource = field.dataSource;
        const dropdownData = field.input?.data as DropdownData<unknown> | undefined;

        if (!dataSource?.cacheKey || !dropdownData) {
          continue;
        }

        callback(field, dropdownData);
      }
    }
  }

  private getDropdownCacheConfig(
    field: FormField<T>,
    changedField: string | null = null,
    options: ReactiveDropdownDependencyLoadOptions = DEFAULT_DROPDOWN_DEPENDENCY_LOAD_OPTIONS,
  ): ReactiveDropdownCacheConfig | null {
    const dataSource = field.dataSource;

    if (!dataSource?.cacheKey) {
      return null;
    }

    return {
      namespace: 'reactive-form-builder',
      name: dataSource.cacheKey,
      endpoint: dataSource.endpoint,
      ttlMs: dataSource.ttlMs ?? 300000,
      params: this.resolveDataSourceParams(dataSource, field, changedField, options),
      userScope: this.getCurrentUserScope(),
      clientScope: this.getCurrentClientScope(),
      appName: this.config.getAppName(),
      cacheEmptyResults: dataSource.cacheEmptyResults === true,
    };
  }

  private resolveDataSourceParams(
    dataSource: ReactiveDropdownDataSource<T> | undefined,
    field: FormField<T>,
    changedField: string | null,
    options: ReactiveDropdownDependencyLoadOptions = DEFAULT_DROPDOWN_DEPENDENCY_LOAD_OPTIONS,
  ): Record<string, unknown> {
    const params = dataSource?.params;

    if (typeof params !== 'function') {
      return params ?? {};
    }

    try {
      return params(this.createDataSourceContext(field, changedField, this.resolveChangeOrigin(options)));
    } catch (error: unknown) {
      console.warn('[ReactiveDropdownEngine] dataSource.params() failed. Cache params fallback to empty object.', error);
      return {};
    }
  }

  private getCurrentUserScope(): string | number {
    try {
      const user = this.config.storage?.getUser();

      return user?.id ?? user?.nombre ?? 'anonymous';
    } catch {
      return 'anonymous';
    }
  }

  private getCurrentClientScope(): string | number {
    try {
      const client = this.config.storage?.getClient();

      return client?.id ?? client?.nombre ?? 'all-clients';
    } catch {
      return 'all-clients';
    }
  }

  private toErrorMessage(error: unknown): string {
    const technicalMessage = this.resolveTechnicalErrorMessage(error);
    const trimmedMessage = technicalMessage.trim();

    if (trimmedMessage !== '') {
      return `No hemos podido cargar este dato. Revisa la conexión o inténtalo de nuevo. Detalle: ${trimmedMessage}`;
    }

    return 'No hemos podido cargar las opciones del desplegable. Revisa la conexión o inténtalo de nuevo.';
  }

  private resolveTechnicalErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message.trim() !== '') {
      return error.message;
    }

    if (error !== null && typeof error === 'object') {
      const record = error as Record<string, unknown>;
      const nestedError = record['error'];

      return [
        record['message'],
        record['statusText'],
        this.readNestedErrorValue(nestedError, 'statusMessage'),
        this.readNestedErrorValue(nestedError, 'reason'),
        this.readNestedErrorValue(nestedError, 'errorCode'),
        record['url'],
      ]
        .map((value) => String(value ?? '').trim())
        .filter((value) => value !== '')
        .join(' - ');
    }

    return String(error ?? '');
  }

  private readNestedErrorValue(error: unknown, key: string): unknown {
    return error !== null && typeof error === 'object'
      ? (error as Record<string, unknown>)[key]
      : null;
  }
}
